import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { App, Button, Form, Space } from 'antd'
import { ArrowLeft, Save } from 'lucide-react'
import {
  serviceOrderCreateSchema,
  type OrderGroup,
  type ServiceOrderItemInput
} from '@super-mega/server/contracts/order'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { CREATE_INITIAL_VALUES } from '@/features/order/const/service-order-create-form.ts'
import {
  createOrderSubmissionKey,
  enterpriseSubmissionNotice
} from '@/features/order/utils/order-enterprise-selection.ts'
import { formatSalespersonOption } from '@/features/order/utils/service-order-form.ts'
import { useDebouncedValue } from '@/hooks/use-debounced-value.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage, getFirstSchemaError } from '@/utils/error.ts'
import { mergeNumberOptions, mergeOptions } from '@/utils/select-options.ts'
import { OrderGroupSelectorModal } from './order-group-selector-modal.tsx'
import type { OrderListSearch } from './order-list-search.ts'
import { QuickCustomerModal } from './order-ownership-quick-create-modal.tsx'
import { ServiceOrderCreateItemsFields } from './service-order-create-items-fields.tsx'
import { ServiceOrderCreateOwnershipFields } from './service-order-create-ownership-fields.tsx'
import type {
  BusinessOption,
  CustomerOption,
  NumberOption,
  OrderGroupMode,
  SalespersonOption,
  ServiceOrderCreateFormValues
} from './service-order-form.types.ts'
import { defaultServiceTerm } from './service-term-fields.tsx'
import { showOrderFormErrors } from './utils/order-form-validation.ts'

export function ServiceOrderCreateForm({
  returnSearch,
  initialCustomer,
  canCreateBusiness = false,
  canCreateCustomer = false
}: {
  returnSearch: OrderListSearch
  initialCustomer?: CustomerOption
  canCreateBusiness?: boolean
  canCreateCustomer?: boolean
}) {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const [form] = Form.useForm<ServiceOrderCreateFormValues>()
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [customerSearch, setCustomerSearch] = useState<string>()
  const [salespersonSearch, setSalespersonSearch] = useState<string>()
  const [businessSearch, setBusinessSearch] = useState<string>()
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | undefined>(initialCustomer)
  const [selectedSalesperson, setSelectedSalesperson] = useState<SalespersonOption | undefined>(
    initialCustomer?.salesperson ?? undefined
  )
  const [selectedBusinesses, setSelectedBusinesses] = useState<Record<number, BusinessOption>>({})
  const [selectedOrderGroup, setSelectedOrderGroup] = useState<OrderGroup>()
  const [orderGroupSelectorOpen, setOrderGroupSelectorOpen] = useState(false)
  const customerKeyword = useDebouncedValue(customerSearch)
  const salespersonKeyword = useDebouncedValue(salespersonSearch)
  const businessKeyword = useDebouncedValue(businessSearch)
  const customerId = Form.useWatch('customerId', form)
  const [getSubmissionKey] = useState(createOrderSubmissionKey)
  const [confirming, setConfirming] = useState(false)
  const [pendingEnterprises, setPendingEnterprises] = useState<Record<number, boolean>>({})
  const enterprisePending = Object.values(pendingEnterprises).some(Boolean)
  const onEnterprisePendingChange = useCallback((key: number, pending: boolean) => {
    setPendingEnterprises((previous) => (previous[key] === pending ? previous : { ...previous, [key]: pending }))
  }, [])
  const items = Form.useWatch<ServiceOrderCreateFormValues['items'] | undefined>('items', form)
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({ purpose: 'order.service.read', keyword: customerKeyword }),
    staleTime: 60_000
  })
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({ purpose: 'order.service.read', keyword: salespersonKeyword }),
    staleTime: 60_000
  })
  const businesses = useQuery({
    ...trpc.lookup.businesses.queryOptions({
      purpose: 'order.service.read',
      keyword: businessKeyword,
      type: 'valueAdded'
    }),
    staleTime: 60_000
  })
  const organizations = useQuery({
    ...trpc.lookup.organizations.queryOptions({ purpose: 'order.service.read' }),
    staleTime: 60_000
  })
  const createServiceOrders = useMutation(trpc.order.service.create.mutationOptions())
  const isSaving = confirming || createServiceOrders.isPending

  const customerOptions = mergeNumberOptions(
    selectedCustomer,
    (customers.data ?? []).map(({ label, value, salesperson }) => ({ label, value, salesperson }))
  )
  const salespersonOptions = mergeNumberOptions(
    selectedSalesperson,
    (salespeople.data ?? []).map(({ label, value, isDisable }) => ({ label, value, isDisable }))
  ).map(formatSalespersonOption)
  const businessOptions = mergeOptions(Object.values(selectedBusinesses), businesses.data ?? [])
  const organizationOptions: NumberOption[] = (organizations.data?.data ?? []).map(({ id, name }) => ({
    label: name,
    value: id
  }))

  const handleCustomerChange = (value: number, newCustomer?: CustomerOption) => {
    const selected = newCustomer ?? customerOptions.find((option) => option.value === value)
    setSelectedCustomer(selected)
    setSelectedSalesperson(selected?.salesperson ?? undefined)
    form.setFieldValue('salespersonId', selected?.salesperson?.value)
    form.setFieldValue(
      'items',
      form.getFieldValue('items').map((item: ServiceOrderItemInput) => ({
        ...item,
        subjectId: undefined,
        enterpriseSelection: { mode: 'none' }
      }))
    )
    form.setFieldsValue({ orderGroupId: undefined, orderGroupMode: 'none', orderGroupName: '' })
    setSelectedOrderGroup(undefined)
    setOrderGroupSelectorOpen(false)
  }
  const openQuickCustomer = () => {
    if (items?.some((item) => item.enterpriseSelection && item.enterpriseSelection.mode !== 'none')) {
      modal.confirm({
        title: '创建并切换客户？',
        content: '创建成功后将切换客户，并清空服务对象资料和订单组，其他订单内容保留。',
        onOk: () => setCreatingCustomer(true)
      })
    } else {
      setCreatingCustomer(true)
    }
  }
  const goBack = () => {
    void navigate({ search: returnSearch, to: '/orders/service' })
  }

  const restore = () => {
    form.resetFields()
    setSelectedOrderGroup(undefined)
    setOrderGroupSelectorOpen(false)
  }

  const save = async (values: ServiceOrderCreateFormValues) => {
    if (enterprisePending) {
      return false
    }
    if (!values.customerId || !values.organizationId) {
      return false
    }

    const itemPayloads: ServiceOrderItemInput[] = []
    for (const [index, item] of values.items.entries()) {
      if (item.businessId === undefined) {
        showOrderFormErrors(form, [{ path: ['items', index, 'businessId'], message: '请选择服务业务' }])
        return false
      }
      if (!item.term && item.agreedAmount === undefined) {
        showOrderFormErrors(form, [{ path: ['items', index, 'agreedAmount'], message: '请输入应收金额' }])
        return false
      }
      const itemPayload: ServiceOrderItemInput = {
        enterpriseSelection: item.enterpriseSelection ?? { mode: 'none' },
        businessId: item.businessId,
        fileIds: item.fileIds,
        note: item.note?.trim() || null,
        contractSettings: item.contractSettings,
        status: 'pending',
        term: item.term ?? null,
        agreedAmount: item.term ? null : item.agreedAmount
      }
      if (!item.term) itemPayload.costAmount = item.costAmount ?? null
      itemPayloads.push(itemPayload)
    }

    if (itemPayloads.length === 0) {
      return false
    }

    let orderGroup
    if (values.orderGroupMode === 'existing') {
      orderGroup = { id: values.orderGroupId, mode: 'existing' as const }
    } else if (values.orderGroupMode === 'new') {
      orderGroup = { mode: 'new' as const, name: values.orderGroupName?.trim() || undefined }
    }
    if (values.orderGroupMode === 'existing' && !values.orderGroupId) {
      showOrderFormErrors(form, [{ path: ['orderGroupId'], message: '请选择已有订单组' }])
      void message.error('请选择已有订单组')
      return false
    }

    try {
      const notice = enterpriseSubmissionNotice(values.items.map((item) => item.enterpriseSelection))
      if (
        notice &&
        !(await modal.confirm({
          title: '确认保存订单及企业资料',
          content: notice,
          okText: '确认保存',
          cancelText: '继续编辑'
        }))
      ) {
        return false
      }
      const payload = {
        customerId: values.customerId,
        organizationId: values.organizationId,
        orderGroup,
        salespersonId: values.salespersonId,
        items: itemPayloads
      }
      const parsed = serviceOrderCreateSchema.safeParse({
        ...payload,
        idempotencyKey: getSubmissionKey(payload)
      })
      if (!parsed.success) {
        showOrderFormErrors(form, parsed.error.issues)
        void message.error(getFirstSchemaError(parsed.error))
        return false
      }
      const created = await createServiceOrders.mutateAsync(parsed.data)
      void message.success(`已创建 ${created.length} 个服务订单，并生成费用草稿`)
      await Promise.all([
        refreshMoneyQueries(queryClient, trpc),
        queryClient.invalidateQueries(trpc.order.subjects.options.queryFilter()),
        queryClient.invalidateQueries(trpc.order.subjects.list.queryFilter()),
        queryClient.invalidateQueries(trpc.order.subjects.detail.queryFilter()),
        ...(notice
          ? [
              queryClient.invalidateQueries(trpc.company.reusable.queryFilter()),
              queryClient.invalidateQueries(trpc.company.search.queryFilter()),
              queryClient.invalidateQueries(trpc.identity.reusable.queryFilter()),
              queryClient.invalidateQueries(trpc.identity.search.queryFilter()),
              queryClient.invalidateQueries(trpc.license.list.queryFilter())
            ]
          : []),
        queryClient.invalidateQueries(trpc.order.groups.list.queryFilter())
      ])
      goBack()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '服务订单创建失败'))
      return false
    }
  }

  return (
    <FormPageContainer
      breadcrumbRender={false}
      content="选择客户和服务业务，确认金额或服务期限后创建订单。"
      title="新建服务订单"
    >
      <Form<ServiceOrderCreateFormValues>
        disabled={isSaving}
        form={form}
        id="service-order-create-form"
        initialValues={{
          ...CREATE_INITIAL_VALUES,
          customerId: initialCustomer?.value,
          salespersonId: initialCustomer?.salesperson?.value
        }}
        layout="vertical"
        onFinish={(values) => {
          if (isSaving || enterprisePending) {
            return
          }
          setConfirming(true)
          void save(values).finally(() => setConfirming(false))
        }}
        scrollToFirstError={FORM_ERROR_SCROLL_OPTIONS}
      >
        <div className="flex flex-col gap-5">
          <ServiceOrderCreateOwnershipFields
            customerId={customerId}
            customerLoading={customers.isLoading}
            customerOptions={customerOptions}
            disabled={isSaving}
            hasEnterpriseSelection={items?.some(
              (item) => item.enterpriseSelection && item.enterpriseSelection.mode !== 'none'
            )}
            onCreateCustomer={canCreateCustomer ? openQuickCustomer : undefined}
            onCustomerChange={handleCustomerChange}
            onCustomerSearch={setCustomerSearch}
            onClearOrderGroup={() => {
              form.setFieldsValue({ orderGroupId: undefined, orderGroupMode: 'none', orderGroupName: '' })
              setSelectedOrderGroup(undefined)
            }}
            onOpenOrderGroupSelector={() => setOrderGroupSelectorOpen(true)}
            onOrderGroupModeChange={(mode: OrderGroupMode) => {
              form.setFieldsValue({
                orderGroupId: undefined,
                orderGroupMode: mode,
                orderGroupName: mode === 'new' ? '' : undefined
              })
              setSelectedOrderGroup(undefined)
            }}
            onSalespersonChange={(value) => {
              const selected = salespersonOptions.find((option) => option.value === value)
              if (selected) {
                setSelectedSalesperson(selected)
              }
            }}
            onSalespersonSearch={setSalespersonSearch}
            organizationLoading={organizations.isLoading}
            organizationOptions={organizationOptions}
            orderGroup={selectedOrderGroup}
            salespersonLoading={salespeople.isLoading}
            salespersonOptions={salespersonOptions}
          />

          <ServiceOrderCreateItemsFields
            canCreateBusiness={canCreateBusiness}
            onEnterprisePendingChange={onEnterprisePendingChange}
            customerId={customerId}
            businessLoading={businesses.isLoading}
            businessOptions={businessOptions}
            disabled={isSaving}
            onBusinessChange={(fieldName, value, createdOption) => {
              const selected = createdOption ?? businessOptions.find((option) => option.value === value)
              if (selected) {
                setSelectedBusinesses((previous) => ({ ...previous, [value]: selected }))
              }
              if (selected?.defaultServiceMode === 'term') {
                const term = defaultServiceTerm()
                term.rules[0].unitPrice = Number(selected.referencePrice ?? 0)
                form.setFieldValue(['items', fieldName, 'term'], term)
                form.setFieldValue(['items', fieldName, 'agreedAmount'], undefined)
              } else {
                form.setFieldValue(['items', fieldName, 'term'], null)
                form.setFieldValue(
                  ['items', fieldName, 'agreedAmount'],
                  selected?.referencePrice == null ? undefined : Number(selected.referencePrice)
                )
              }
            }}
            onBusinessSearch={setBusinessSearch}
          />

          <div className="border-border bg-surface/95 sticky bottom-3 z-10 flex items-center justify-between gap-3 rounded-lg border px-4 py-3 shadow-xs backdrop-blur">
            <Button icon={<ArrowLeft aria-hidden size={16} />} onClick={goBack}>
              返回订单列表
            </Button>
            <Space>
              <Button disabled={isSaving} onClick={restore}>
                重置
              </Button>
              <Authorized permissions={['order.service.create']}>
                <Button
                  htmlType="submit"
                  disabled={enterprisePending}
                  icon={<Save aria-hidden size={16} />}
                  loading={isSaving}
                  type="primary"
                >
                  创建服务订单
                </Button>
              </Authorized>
            </Space>
          </div>
        </div>
      </Form>
      <OrderGroupSelectorModal
        customerId={customerId}
        initialSelectedGroup={selectedOrderGroup}
        onCancel={() => setOrderGroupSelectorOpen(false)}
        onSelect={(group) => {
          form.setFieldsValue({ orderGroupId: group.id, orderGroupMode: 'existing', orderGroupName: undefined })
          setSelectedOrderGroup(group)
          setOrderGroupSelectorOpen(false)
        }}
        open={orderGroupSelectorOpen}
      />
      {creatingCustomer && (
        <QuickCustomerModal
          onClose={() => setCreatingCustomer(false)}
          onCreated={(customer) => {
            form.setFieldValue('customerId', customer.value)
            handleCustomerChange(customer.value, customer)
            setCustomerSearch(undefined)
          }}
        />
      )}
    </FormPageContainer>
  )
}
